import React, { CSSProperties, useCallback, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { SelectionType } from '../enums/SelectionType.enum';
import { PossibleStyle, useProductStyles } from '../hooks/useProductStyles';
import { SizingToolState } from '../models/actionStates';
import ProductFormButton from '../product_detail/components/ProductFormButton';
import { sizingToolState } from '../recoil/selectors';
import { DivButton } from '../root/components/DivButton';
import { ProductColorSwatches } from './ProductColorSwatches';
import { ProductFitSwatches } from './ProductFitSwatches';
import { ProductSizeSwatches } from './ProductSizeSwatches';
import { ProductControlProps } from './ProductControls';
import { QuantityDropdown } from './QuantityDropdown';
import { useProductSelection } from '../hooks/useProductSelection';
import { allProductsState } from '../recoil/atoms';
import { Button } from '../root/components/buttons/Button';
import { ProductGhostSelector } from './ProductGhostSelector';
import styles from './ProductControlsDesktop.module.scss';

const baseToggleStyle: CSSProperties = {
  padding: '10px 12px',
  fontSize: '13px',
  fontWeight: 600,
  borderRadius: '2px',
};

const activeStyle: CSSProperties = {
  ...baseToggleStyle,
  border: '1px solid black',
  zIndex: 1,
  color: '#6666CC',
};

const inactiveStyle: CSSProperties = {
  ...baseToggleStyle,
  border: '1px solid transparent',
};

const StyleToggle: React.FC<{ productStyles: PossibleStyle[]; selectionType: SelectionType; selectionKey: string }> = ({
  productStyles,
  selectionType,
  selectionKey,
}) => {
  const { setHandle, handle, color } = useProductSelection(selectionType, selectionKey);
  const allProducts = useRecoilValue(allProductsState);
  const currentStyle = productStyles.find((s) => s.handle === handle);

  return (
    <div className={styles.toggleContainer}>
      <div className={styles.toggleTextWrapper}>
        <span>STYLE:&nbsp;</span>
        <span className={styles.title}>{currentStyle?.title?.toUpperCase()}</span>
      </div>
      <div className={styles.mannequinButtonsWrapper}>
        {productStyles.map((s, i) => {
          const isActive = s.handle === handle;
          const product = allProducts[s.handle];
          return (
            <DivButton key={i} onClick={() => setHandle(s.handle)} style={isActive ? activeStyle : inactiveStyle} label={`Select product: ${s.title}`}>
              <ProductGhostSelector product={product} color={color} />
            </DivButton>
          );
        })}
      </div>
    </div>
  );
};

export const ProductControlsDesktop: React.FC<ProductControlProps> = ({ selectionType, style, selectionKey }) => {
  const { product } = useProductSelection(selectionType, selectionKey);
  const productStyles = useProductStyles(product);
  const [quantity, setQuantity] = useState<number>(1);
  const setSizingToolState = useSetRecoilState(sizingToolState);
  const openSizingTool = useCallback(() => {
    setSizingToolState({
      sizingToolState: SizingToolState.Open,
      selectionType,
      selectionKey,
      initialProduct: product,
      product: product,
    });
  }, [setSizingToolState, selectionType, selectionKey, product]);

  return (
    <div style={style}>
      {productStyles?.length > 1 && <StyleToggle productStyles={productStyles} selectionType={selectionType} selectionKey={selectionKey} />}
      <div className={styles.controlsWrapper}>
        <ProductColorSwatches product={product} selectionType={selectionType} selectionKey={selectionKey} isMobile={false} />
        <ProductFitSwatches product={product} selectionType={selectionType} selectionKey={selectionKey} style={{ marginTop: 10 }} />
        <ProductSizeSwatches product={product} selectionType={selectionType} selectionKey={selectionKey} style={{ marginTop: 10, marginBottom: 10 }} />
        <Button onClick={openSizingTool} size={'tiny'} text="Sizing help" noMargin={true} />
        <div className={styles.ctaContainer}>
          <QuantityDropdown style={{ width: 80 }} setQuantity={setQuantity} />
          <ProductFormButton selectionType={selectionType} selectionKey={selectionKey} quantity={quantity} />
        </div>
      </div>
    </div>
  );
};

export default ProductControlsDesktop;
